// Per-host venues and the "don't repeat questions at this venue" window.
// Stored on the host's profile: users/<uid>.venues (array of names) and
// users/<uid>.venueRepeatMonths (0 = forever). The iPad host app reads and writes the same fields.
// Venues are only ever added or hidden (users/<uid>.hiddenVenues); nothing is deleted.
import { useEffect, useState } from "react";
import { doc, onSnapshot, setDoc, arrayUnion } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";

export const TEST_VENUE = "Test Venue";
export const REPEAT_MONTH_OPTIONS = [0, 1, 2, 3, 6, 9, 12, 18, 24];
const REPEAT_KEY = "opt_venue_repeat_months";

export function repeatLabel(months) {
  const m = Number(months) || 0;
  if (m <= 0) return "Forever";
  return m === 1 ? "For 1 month" : `For ${m} months`;
}

/** The host's saved venues (minus hidden ones), in the order they were added. */
export function venueList(profile) {
  const hidden = new Set((profile?.hiddenVenues || []).map((v) => String(v).trim()));
  const seen = new Set();
  const out = [];
  for (const raw of profile?.venues || []) {
    const v = String(raw || "").trim();
    if (!v || v === TEST_VENUE || hidden.has(v) || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

export function repeatMonthsFrom(profile) {
  const n = Number(profile?.venueRepeatMonths);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

/** Repeat window last loaded from the profile (used by the quiz engine, which reads localStorage). */
export function storedRepeatMonths() {
  const n = Number(localStorage.getItem(REPEAT_KEY));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Live profile venues + repeat window for the signed-in host. */
export function useHostVenues() {
  const [uid, setUid] = useState(auth.currentUser?.uid || null);
  const [profile, setProfile] = useState(null);

  useEffect(() => onAuthStateChanged(auth, (u) => setUid(u?.uid || null)), []);

  useEffect(() => {
    if (!uid) { setProfile(null); return undefined; }
    return onSnapshot(doc(db, "users", uid), (snap) => {
      const p = snap.data() || {};
      setProfile(p);
      try { localStorage.setItem(REPEAT_KEY, String(repeatMonthsFrom(p))); } catch {}
    });
  }, [uid]);

  return {
    uid,
    loaded: profile !== null,
    venues: venueList(profile),
    hidden: profile?.hiddenVenues || [],
    repeatMonths: repeatMonthsFrom(profile),
  };
}

export async function addVenue(uid, name, hidden = []) {
  const v = String(name || "").trim();
  if (!uid || !v || v === TEST_VENUE) return;
  const update = { venues: arrayUnion(v) };
  // Adding a venue you'd hidden brings it back.
  if (hidden.some((h) => String(h).trim() === v)) update.hiddenVenues = hidden.filter((h) => String(h).trim() !== v);
  await setDoc(doc(db, "users", uid), update, { merge: true });
}

export async function hideVenue(uid, name) {
  const v = String(name || "").trim();
  if (!uid || !v) return;
  await setDoc(doc(db, "users", uid), { hiddenVenues: arrayUnion(v) }, { merge: true });
}

export async function setRepeatMonths(uid, months) {
  if (!uid) return;
  await setDoc(doc(db, "users", uid), { venueRepeatMonths: Math.max(0, Number(months) || 0) }, { merge: true });
}

function usedAtMs(entry) {
  const u = entry?.usedAt;
  if (!u) return null;
  if (typeof u.toMillis === "function") return u.toMillis();
  if (typeof u.seconds === "number") return u.seconds * 1000;
  const t = new Date(u).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * True when the question was played at this venue inside the repeat window
 * (months 0 = any time). An entry with no date counts as recent, to stay safe.
 */
export function usedAtVenueRecently(usageHistory, venueId, months = storedRepeatMonths()) {
  if (!Array.isArray(usageHistory)) return false;
  const cutoff = months > 0 ? Date.now() - months * 30.44 * 24 * 3600 * 1000 : null;
  return usageHistory.some((entry) => {
    const entryVenue = entry?.location?.venueId ?? (typeof entry?.location === "string" ? entry.location : null);
    if (entryVenue !== venueId) return false;
    if (cutoff == null) return true;
    const t = usedAtMs(entry);
    return t == null || t >= cutoff;
  });
}
